import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { postsApi, api } from '../api/client';
import { processImage } from '../utils/imageProcessing';
import {
  BookOpen, MessageSquareQuote, HeartHandshake, Sparkles, HandHeart, Heart,
  Globe, UserCheck, Users, Building2,
  Pencil, X,
  Camera, Video, AlertTriangle, Flame,
  ChevronDown, Hash, Bookmark, Plus, Search,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { buildGreeting } from '../utils/greeting';
import './FeedScreen.css';
import PageGuide from '../components/PageGuide';
import PostCard, { getInitials, useMentionAutocomplete } from '../components/PostCard';

// ── Static metadata ────────────────────────────────────────────────────────────
// bannerClass drives CSS gradient classes; Icon is used in banner + form picker
const POST_TYPE_ICONS = [
  { value: 'reflection', Icon: MessageSquareQuote },
  { value: 'scripture',  Icon: BookOpen           },
  { value: 'prayer',     Icon: HeartHandshake     },
  { value: 'praise',     Icon: Sparkles           },
  { value: 'surrender',  Icon: HandHeart          },
];
const VIS_ICONS = [
  { value: 'public',      Icon: Globe      },
  { value: 'connections', Icon: UserCheck  },
  { value: 'friends',     Icon: Users      },
  { value: 'groups',      Icon: Building2  },
];
const FILTER_ICONS = [
  { value: 'all'             },
  { value: 'scripture',       Icon: BookOpen           },
  { value: 'reflection',      Icon: MessageSquareQuote },
  { value: 'prayer',          Icon: HeartHandshake     },
  { value: 'praise',          Icon: Sparkles           },
  { value: 'surrender',       Icon: HandHeart          },
  { value: 'prayer_request',  Icon: Heart              },
];

const DEFAULT_BIBLE_ID = '78a9f6124f344018-01'; // KJV

function CreatePostModal({ onClose, onCreate, initialType, initialContent }) {
  const { t } = useTranslation();
  const [type, setType]           = useState(initialType || 'reflection');
  const [visibility, setVis]      = useState('public');
  const [content, setContent]     = useState(initialContent || '');
  const [mediaFiles, setMediaFiles]     = useState([]);   // {file, preview, id}
  const [videoFile, setVideoFile]       = useState(null);  // {file, preview}
  const [mediaMode, setMediaMode]       = useState(null);  // 'images' | 'video' | null
  const [uploading, setUploading]       = useState(false);
  const [error, setError]               = useState('');
  const [dragIdx, setDragIdx]           = useState(null);
  // ── Structured field state ────────────────────────────────────────────────
  const [scriptureRef, setScriptureRef]     = useState('');
  const [scriptureVerse, setScriptureVerse] = useState(null); // {ref, text}
  const [verseLoading, setVerseLoading]     = useState(false);
  const [praisingWhat, setPraisingWhat]     = useState('');
  const [surrenderingWhat, setSurrenderingWhat] = useState('');
  const [prayerText, setPrayerText]         = useState('');
  const [showPrayerText, setShowPrayerText] = useState(false);

  const imageRef  = useRef();
  const videoRef  = useRef();
  const textareaRef = useRef();
  const MAX_IMAGES = 10;

  const { suggestions, mentionQuery, selectUser, resolvedMentions } = useMentionAutocomplete(content, setContent);

  const POST_TYPES = POST_TYPE_ICONS.map(({ value, Icon }) => ({
    value, Icon, label: t(`feed.postTypes.${value}`),
  }));
  const VISIBILITY_OPTIONS = VIS_ICONS.map(({ value, Icon }) => ({
    value, Icon, label: t(`feed.visibility.${value}`),
  }));
  const placeholders = {
    scripture: t('feed.placeholders.scripture'),
    prayer:    t('feed.placeholders.prayer'),
    reflection:t('feed.placeholders.reflection'),
    praise:    t('feed.placeholders.praise'),
    surrender: t('feed.placeholders.surrender'),
  };

  function handleTypeChange(newType) {
    setType(newType);
    setScriptureRef(''); setScriptureVerse(null);
    setPraisingWhat(''); setSurrenderingWhat('');
    setPrayerText(''); setShowPrayerText(false);
    setError('');
  }

  async function lookupVerse() {
    if (!scriptureRef.trim()) return;
    setVerseLoading(true); setError('');
    try {
      const d = await api.get(
        `/api/bible/passage?ref=${encodeURIComponent(scriptureRef.trim())}&bibleId=${DEFAULT_BIBLE_ID}`
      );
      setScriptureVerse({ ref: d.reference, text: d.content });
    } catch(err) {
      setError(err.message || t('feed.verseNotFound'));
    } finally { setVerseLoading(false); }
  }

  function handleImageSelect(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = MAX_IMAGES - mediaFiles.length;
    const toAdd = files.slice(0, remaining);
    toAdd.forEach(f => {
      const id = Math.random().toString(36).slice(2);
      const reader = new FileReader();
      reader.onload = ev => setMediaFiles(prev => [...prev, { file: f, preview: ev.target.result, id }]);
      reader.readAsDataURL(f);
    });
    setMediaMode('images');
    e.target.value = '';
  }
  function handleVideoSelect(e) {
    const f = e.target.files[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setVideoFile({ file: f, preview: ev.target.result });
    reader.readAsDataURL(f);
    setMediaMode('video');
    e.target.value = '';
  }
  function removeImage(id) {
    setMediaFiles(prev => {
      const next = prev.filter(m => m.id !== id);
      if (!next.length) setMediaMode(null);
      return next;
    });
  }
  function removeVideo() { setVideoFile(null); setMediaMode(null); }
  function moveImage(from, to) {
    setMediaFiles(prev => {
      const arr = [...prev];
      const [item] = arr.splice(from, 1);
      arr.splice(to, 0, item);
      return arr;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Type-specific validation
    if (type === 'praise' && !praisingWhat.trim()) {
      setError(t('feed.praiseMissing')); return;
    }
    if (type === 'surrender' && !surrenderingWhat.trim()) {
      setError(t('feed.surrenderMissing')); return;
    }
    // For praise/surrender the structured field IS the content; textarea optional
    const hasStructured = (type === 'praise' && praisingWhat.trim()) || (type === 'surrender' && surrenderingWhat.trim());
    if (!content.trim() && !hasStructured) { setError(t('feed.writeSomething')); return; }

    // Assemble final content with structured markers
    let finalContent = content.trim();
    if (type === 'praise' && praisingWhat.trim()) {
      const marker = `[PRAISING]${JSON.stringify({ what: praisingWhat.trim() })}[/PRAISING]`;
      finalContent = finalContent ? `${marker}\n${finalContent}` : marker;
    }
    if (type === 'surrender' && surrenderingWhat.trim()) {
      const marker = `[SURRENDERING]${JSON.stringify({ what: surrenderingWhat.trim() })}[/SURRENDERING]`;
      finalContent = finalContent ? `${marker}\n${finalContent}` : marker;
    }
    if (type === 'scripture' && scriptureVerse) {
      finalContent += `\n[SCRIPTURE]${JSON.stringify({ ref: scriptureVerse.ref, text: scriptureVerse.text })}[/SCRIPTURE]`;
    }
    if (type === 'prayer' && prayerText.trim()) {
      finalContent += `\n[PRAYER_TEXT]${JSON.stringify({ text: prayerText.trim() })}[/PRAYER_TEXT]`;
    }

    setUploading(true); setError('');
    try {
      let mediaUrls = [], mediaUrl = null, mediaType = null;
      if (mediaMode === 'images' && mediaFiles.length) {
        const uploads = await Promise.all(mediaFiles.map(async m => {
          const { blob, type: blobType } = await processImage(m.file, { preset: 'post' });
          const { uploadUrl, fileUrl } = await api.post('/api/posts/media-presign', { fileType: blobType });
          await fetch(uploadUrl, { method:'PUT', body: blob, headers:{'Content-Type': blobType} });
          return fileUrl;
        }));
        mediaUrls = uploads; mediaUrl = uploads[0]; mediaType = 'image';
      } else if (mediaMode === 'video' && videoFile) {
        const { uploadUrl, fileUrl } = await api.post('/api/posts/media-presign', { fileType: videoFile.file.type });
        await fetch(uploadUrl, { method:'PUT', body: videoFile.file, headers:{'Content-Type': videoFile.file.type} });
        mediaUrl = fileUrl; mediaType = 'video'; mediaUrls = [fileUrl];
      }
      const d = await postsApi.create({ type, visibility, content: finalContent, mediaUrl, mediaType, mediaUrls });
      onCreate(d.post); onClose();
    } catch(err) { setError(err.message || t('feed.failedPost')); }
    finally { setUploading(false); }
  }

  const currentType = POST_TYPES.find(pt=>pt.value===type);
  const currentVis  = VISIBILITY_OPTIONS.find(v=>v.value===visibility);

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{t('feed.modalTitle')}</span>
          <button className="modal-close" onClick={onClose}><X size={16}/></button>
        </div>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="post-form-selects">
            <div className="post-select-wrap">
              <label className="post-select-label">{t('feed.postType')}</label>
              <div className="post-select-box" data-label={currentType?.label || ''}>
                {currentType && <currentType.Icon size={14} strokeWidth={2}/>}
                <select value={type} onChange={e=>handleTypeChange(e.target.value)} className="post-native-select">
                  {POST_TYPES.map(({value,label})=><option key={value} value={value}>{label}</option>)}
                </select>
                <ChevronDown size={14} className="post-select-chevron"/>
              </div>
            </div>
            <div className="post-select-wrap">
              <label className="post-select-label">{t('feed.visibleTo')}</label>
              <div className="post-select-box" data-label={currentVis?.label || ''}>
                {currentVis && <currentVis.Icon size={14} strokeWidth={2}/>}
                <select value={visibility} onChange={e=>setVis(e.target.value)} className="post-native-select">
                  {VISIBILITY_OPTIONS.map(({value,label})=><option key={value} value={value}>{label}</option>)}
                </select>
                <ChevronDown size={14} className="post-select-chevron"/>
              </div>
            </div>
          </div>

          {/* ── Praise: What are you praising God for? ── */}
          {type === 'praise' && (
            <div className="structured-input-row structured-input-row--praise">
              <label className="structured-input-label">{t('feed.praiseWhatLabel')} *</label>
              <input
                type="text"
                className="structured-input structured-input--praise"
                placeholder={t('feed.praiseWhatPlaceholder')}
                value={praisingWhat}
                onChange={e => setPraisingWhat(e.target.value)}
                maxLength={200}
              />
            </div>
          )}

          {/* ── Surrender: What are you surrendering? ── */}
          {type === 'surrender' && (
            <div className="structured-input-row structured-input-row--surrender">
              <label className="structured-input-label">{t('feed.surrenderWhatLabel')} *</label>
              <input
                type="text"
                className="structured-input structured-input--surrender"
                placeholder={t('feed.surrenderWhatPlaceholder')}
                value={surrenderingWhat}
                onChange={e => setSurrenderingWhat(e.target.value)}
                maxLength={200}
              />
            </div>
          )}

          {/* ── Scripture: Verse lookup ── */}
          {type === 'scripture' && (
            <div className="verse-lookup-section">
              <label className="structured-input-label">{t('feed.verseLabel')}</label>
              {!scriptureVerse ? (
                <div className="verse-lookup-row">
                  <input
                    type="text"
                    className="verse-lookup-input"
                    placeholder={t('feed.versePlaceholder')}
                    value={scriptureRef}
                    onChange={e => setScriptureRef(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), lookupVerse())}
                  />
                  <button type="button" className="verse-lookup-btn"
                    onClick={lookupVerse}
                    disabled={verseLoading || !scriptureRef.trim()}>
                    {verseLoading
                      ? <span className="spinner" style={{width:13,height:13,borderWidth:1.5}}/>
                      : <><Search size={13}/> {t('feed.verseLookup')}</>}
                  </button>
                </div>
              ) : (
                <div className="verse-preview-card">
                  <p className="verse-preview-text">"{scriptureVerse.text}"</p>
                  <p className="verse-preview-ref">— {scriptureVerse.ref}</p>
                  <button type="button" className="verse-preview-clear"
                    onClick={() => { setScriptureVerse(null); setScriptureRef(''); }}>
                    <X size={11}/>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Prayer: Optional prayer text ── */}
          {type === 'prayer' && (
            <div className="structured-input-row">
              {!showPrayerText ? (
                <button type="button" className="add-prayer-text-btn"
                  onClick={() => setShowPrayerText(true)}>
                  + {t('feed.addPrayerText')}
                </button>
              ) : (
                <>
                  <label className="structured-input-label">{t('feed.prayerTextLabel')}</label>
                  <textarea
                    className="create-textarea"
                    placeholder={t('feed.prayerTextPlaceholder')}
                    rows={3}
                    value={prayerText}
                    onChange={e => setPrayerText(e.target.value)}
                    style={{marginBottom:0, minHeight:80}}
                  />
                </>
              )}
            </div>
          )}

          <div className="post-textarea-wrap">
            <textarea ref={textareaRef} className="create-textarea" rows={5} value={content}
              onChange={e=>setContent(e.target.value)} placeholder={placeholders[type]}/>
            {suggestions.length > 0 && (
              <div className="mention-dropdown">
                {suggestions.map(u => (
                  <button key={u.id} type="button" className="mention-option"
                    onMouseDown={e => { e.preventDefault(); selectUser(u); }}>
                    <div className="mention-avatar">{getInitials(u.name)}</div>
                    <div className="mention-info">
                      <span className="mention-name">{u.name}</span>
                      <span className="mention-handle">@{u.username}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="post-hints">
              <span>{t('feed.mentionHint')}</span>
            </div>
            {resolvedMentions.length > 0 && (
              <div className="mention-chips">
                {resolvedMentions.map(u => (
                  <div key={u.username} className="mention-chip">
                    <div className="mention-chip-avatar">
                      {u.avatar
                        ? <img src={u.avatar} alt="" className="mention-chip-avatar-img"/>
                        : <span>{getInitials(u.name)}</span>}
                    </div>
                    <span className="mention-chip-name">{u.name}</span>
                    <span className="mention-chip-handle">@{u.username}</span>
                    <span className="mention-chip-linked">✓ linked</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {mediaMode === 'video' && videoFile && (
            <div className="media-preview">
              <video src={videoFile.preview} className="media-preview-el" controls/>
              <button type="button" className="media-remove" onClick={removeVideo}><X size={12}/></button>
            </div>
          )}
          {mediaMode === 'images' && mediaFiles.length > 0 && (
            <div className="media-preview-grid">
              {mediaFiles.map((m, idx) => (
                <div key={m.id} className="media-preview-thumb"
                  draggable onDragStart={()=>setDragIdx(idx)}
                  onDragOver={e=>{e.preventDefault();}}
                  onDrop={()=>{ if(dragIdx!==null&&dragIdx!==idx){moveImage(dragIdx,idx);} setDragIdx(null); }}>
                  <img src={m.preview} alt="" className="media-preview-thumb-img"/>
                  <button type="button" className="media-thumb-remove" onClick={()=>removeImage(m.id)}>
                    <X size={10}/>
                  </button>
                  {idx===0 && <span className="media-thumb-cover">Cover</span>}
                </div>
              ))}
              {mediaFiles.length < MAX_IMAGES && (
                <button type="button" className="media-preview-add" onClick={()=>imageRef.current.click()}>
                  <Plus size={20}/> <span>{MAX_IMAGES - mediaFiles.length} left</span>
                </button>
              )}
            </div>
          )}

          <div className="create-bottom-row">
            <div className="media-btns">
              <input ref={imageRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={handleImageSelect}/>
              <input ref={videoRef} type="file" accept="video/*" style={{display:'none'}} onChange={handleVideoSelect}/>
              <button type="button" className={`media-btn${mediaMode==='images'?' active':''}`}
                disabled={mediaMode==='video'}
                onClick={()=>imageRef.current.click()}>
                <Camera size={18} strokeWidth={1.75}/>
                <span>{mediaFiles.length > 0 ? `${mediaFiles.length} photo${mediaFiles.length>1?'s':''}` : t('common.photo')}</span>
              </button>
              <button type="button" className={`media-btn${mediaMode==='video'?' active':''}`}
                disabled={mediaMode==='images'}
                onClick={()=>videoRef.current.click()}>
                <Video size={18} strokeWidth={1.75}/> <span>{t('common.video')}</span>
              </button>
            </div>
            <button className="btn btn-primary" type="submit" disabled={uploading}>
              {uploading ? <span className="spinner"/> : t('common.post')}
            </button>
          </div>
        </form>
      </div>
    </div>, document.body
  );
}

export default function FeedScreen() {
  // Rendered by HomeLayout/GrowLayout and placed here so the strip sits under
  // this screen's title, matching Prayer and People. Null when this screen is
  // reached outside its section layout.
  const { sectionTabs } = useOutletContext() || {};
  const { t, lang } = useTranslation();
  const { dbUser } = useAuth();
  const greetingText = buildGreeting(dbUser?.firstName, lang);
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]       = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError]           = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter]         = useState('all');
  const [hashtagFilter, setHashtagFilter] = useState(null);
  const [dbUserId, setDbUserId]     = useState(null);
  const [feedTab, setFeedTab]       = useState('feed'); // 'feed' | 'saved'
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [highlightPostId, setHighlightPostId] = useState(searchParams.get('postId'));
  const [composeType, setComposeType] = useState(null);
  const [composeText, setComposeText] = useState('');
  const highlightRef = React.useRef(null);

  // Deep-link composer (e.g. ?compose=testimony&text=... from a win share)
  useEffect(() => {
    const compose = searchParams.get('compose');
    if (compose) {
      setComposeType(compose);
      setComposeText(searchParams.get('text') || '');
      setShowCreate(true);
      searchParams.delete('compose');
      searchParams.delete('text');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Scroll to highlighted post when posts load
  useEffect(() => {
    if (highlightPostId && posts.length > 0) {
      // Give DOM time to render
      setTimeout(() => {
        if (highlightRef.current) {
          highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the highlight after 3 seconds
          setTimeout(() => setHighlightPostId(null), 3000);
        }
      }, 200);
    }
  }, [posts, highlightPostId]);

  useEffect(() => {
    api.get('/api/profile').then(d => setDbUserId(d.user?.id)).catch(()=>{});
    loadPosts();
  }, []);

  function buildFeedQuery(f, htag, before) {
    const params = new URLSearchParams();
    if (htag)             params.set('hashtag', htag);
    else if (f !== 'all') params.set('type', f);
    if (before)           params.set('before', before);
    const s = params.toString();
    return s ? `?${s}` : '';
  }

  async function loadPosts(f = filter, htag = hashtagFilter) {
    setLoading(true); setError('');
    try {
      const d = await postsApi.list(buildFeedQuery(f, htag));
      setPosts(d.posts || []);
      setHasMore(!!d.hasMore);
      setNextCursor(d.nextCursor || null);
    } catch (err) { setError(err.message || t('feed.failedLoad')); }
    finally { setLoading(false); }
  }

  async function loadMorePosts() {
    if (!hasMore || loadingMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const d = await postsApi.list(buildFeedQuery(filter, hashtagFilter, nextCursor));
      setPosts(prev => [...prev, ...(d.posts || [])]);
      setHasMore(!!d.hasMore);
      setNextCursor(d.nextCursor || null);
    } catch (err) {
      setError(err.message || t('feed.failedLoad'));
    } finally { setLoadingMore(false); }
  }

  useEffect(() => { loadPosts(filter, hashtagFilter); }, [filter, hashtagFilter]);

  function handleHashtagClick(tag) { setHashtagFilter(tag); setFilter('all'); }
  function clearHashtag() { setHashtagFilter(null); }

  async function handleReact(id, emoji) {
    try {
      const d = await postsApi.react(id, emoji);
      setPosts(ps => ps.map(p => p.id===id ? { ...p, reactions: d.reactions, userReactions: d.userReactions } : p));
    } catch {}
  }
  async function handleDelete(id) {
    if (!window.confirm(t('feed.deleteConfirm'))) return;
    await api.delete(`/api/posts/${id}`);
    setPosts(ps => ps.filter(p=>p.id!==id));
  }
  async function handleEdit(id, content) {
    const d = await api.put(`/api/posts/${id}`, { content });
    setPosts(ps => ps.map(p => p.id===id ? {...p,...d.post} : p));
  }

  async function loadSaved() {
    setSavedLoading(true);
    try {
      const d = await api.get('/api/posts/bookmarks');
      setSavedPosts(d.posts || []);
    } catch {}
    finally { setSavedLoading(false); }
  }

  useEffect(() => { if (feedTab === 'saved') loadSaved(); }, [feedTab]);

  async function handleBookmark(id) {
    try {
      const d = await api.post(`/api/posts/${id}/bookmark`, {});
      setPosts(ps => ps.map(p => p.id === id ? { ...p, bookmarked: d.bookmarked } : p));
      setSavedPosts(sp => sp.filter(p => p.id !== id)); // remove from saved if unbookmarked
    } catch {}
  }

  async function handleRepost(id, quote='') {
    try {
      const d = await api.post(`/api/posts/${id}/repost`, { quote });
      setPosts(ps => [d.post, ...ps]);
      return true;
    } catch(err) {
      if (err.message?.includes('Already reposted')) { alert('You have already reposted this.'); }
      else if (err.message?.includes('own post')) { alert("You can't repost your own post."); }
      else { alert('Failed to repost.'); }
      return false;
    }
  }

  const FILTERS = FILTER_ICONS.map(({ value, Icon }) => ({
    value, Icon,
    label: value === 'all' ? t('common.all') : t(`feed.postTypes.${value}`),
  }));

  // Feed is a tab of the Home section now, so its own Feed / Saved strip is
  // gone — Saved is a labelled toggle beside the page guide instead. Two
  // stacked tab strips is what the navigation restructure set out to remove.
  const savedToggle = (
    <button
      className={`feed-saved-toggle${feedTab === 'saved' ? ' active' : ''}`}
      aria-pressed={feedTab === 'saved'}
      onClick={() => setFeedTab(feedTab === 'saved' ? 'feed' : 'saved')}>
      <Bookmark size={15} strokeWidth={2} fill={feedTab === 'saved' ? 'currentColor' : 'none'} />
      {feedTab === 'saved' ? t('feed.feedTab') : t('feed.savedTab')}
    </button>
  );

  return (
    <div className="page">
      {greetingText && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <p className="feed-greeting">{greetingText}</p>
            <div className="feed-greeting-accent" />
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            {savedToggle}
            <PageGuide screenKey="feed" />
          </div>
        </div>
      )}
      {!greetingText && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          {savedToggle}
          <PageGuide screenKey="feed" />
        </div>
      )}
      {/* Feed has no PageHeader — the greeting above stands in for one — so the
          section strip goes here, after it, for the same title-then-tabs order
          every other section uses. */}
      {sectionTabs}

      {feedTab === 'saved' ? (
        savedLoading ? (
          <div style={{display:'flex',justifyContent:'center',padding:48}}><span className="spinner" style={{width:32,height:32}}/></div>
        ) : savedPosts.length === 0 ? (
          <div className="empty-state">
            <Bookmark size={40} strokeWidth={1.25} color="var(--gray-3)"/>
            <h3>{t('feed.noSavedPosts')}</h3>
            <p>{t('feed.noSavedPostsDesc')}</p>
          </div>
        ) : (
          <div className="feed-list">
            {savedPosts.map(p => (
              <PostCard key={p.id} post={{...p, bookmarked:true}} currentDbUserId={dbUserId}
                onReact={handleReact} onDelete={handleDelete} onEdit={handleEdit}
                onHashtagClick={handleHashtagClick} onBookmark={handleBookmark} onRepost={handleRepost}/>
            ))}
          </div>
        )
      ) : (
      <>
      <div className="compose-prompt card" onClick={()=>setShowCreate(true)}>
        <div className="compose-avatar"><Flame size={20} color="white"/></div>
        <div className="compose-placeholder">{t('feed.composePlaceholder')}</div>
        <div className="compose-actions">
          <Camera size={17} strokeWidth={1.75}/>
          <Video  size={17} strokeWidth={1.75}/>
        </div>
      </div>

      {hashtagFilter && (
        <div className="hashtag-filter-bar">
          <Hash size={13}/> #{hashtagFilter}
          <button className="hashtag-filter-clear" onClick={clearHashtag}><X size={13}/></button>
        </div>
      )}

      {!hashtagFilter && (
        <div className="feed-filters">
          {FILTERS.map(({value,label,Icon})=>(
            <button key={value} className={`feed-filter${filter===value?' active':''}`} onClick={()=>setFilter(value)}>
              {Icon && <Icon size={13} strokeWidth={2}/>} {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:48}}><span className="spinner" style={{width:32,height:32}}/></div>
      ) : error ? (
        <div className="empty-state">
          <AlertTriangle size={36} strokeWidth={1.5} color="var(--text-lt)"/>
          <h3>{t('feed.somethingWrong')}</h3><p>{error}</p>
          <button className="btn btn-primary" onClick={()=>loadPosts()}>{t('common.retry')}</button>
        </div>
      ) : posts.length===0 ? (
        <div className="empty-state">
          <Flame size={40} strokeWidth={1.25} color="var(--orange)"/>
          <h3>{hashtagFilter ? t('feed.noPostsHashtag', hashtagFilter) : t('feed.noPosts')}</h3>
          <p>{t('feed.beFirst')}</p>
          {!hashtagFilter && <button className="btn btn-primary" onClick={()=>setShowCreate(true)}>{t('feed.shareSomething')}</button>}
        </div>
      ) : (
        <div className="feed-list">
          {posts.map(p=>{
            const isHighlighted = p.id === highlightPostId;
            return (
              <div key={p.id} ref={isHighlighted ? highlightRef : null}
                className={isHighlighted ? 'feed-post-highlighted' : ''}>
                <PostCard post={p} currentDbUserId={dbUserId}
                  onReact={handleReact} onDelete={handleDelete} onEdit={handleEdit}
                  onHashtagClick={handleHashtagClick} onBookmark={handleBookmark} onRepost={handleRepost}/>
              </div>
            );
          })}
          {hasMore && (
            <div style={{ display:'flex', justifyContent:'center', padding:'16px 0 24px' }}>
              <button className="btn btn-outline" onClick={loadMorePosts} disabled={loadingMore}>
                {loadingMore
                  ? <><span className="spinner" style={{width:14,height:14}}/> {t('feed.loadingMore') || 'Loading…'}</>
                  : (t('feed.loadMore') || 'Load older posts')}
              </button>
            </div>
          )}
        </div>
      )}

      <button className="fab" onClick={()=>setShowCreate(true)}>
        <Pencil size={20} strokeWidth={2}/>
      </button>

      {showCreate && <CreatePostModal initialType={composeType} initialContent={composeText} onClose={()=>{ setShowCreate(false); setComposeType(null); setComposeText(''); }} onCreate={p=>setPosts(ps=>[p,...ps])}/>}
      </>
      )}
    </div>
  );
}
